import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of, catchError, map, tap, switchMap } from 'rxjs';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';


import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

import { GoodsShopControllerService } from '@app/api/goods-service/api/goodsShopController.service';
import { GoodsShop } from '@app/api/goods-service/model/goodsShop';
import { PageRequestDTO } from '@app/api/goods-service/model/pageRequestDTO';
import { SpuControllerService } from '@app/api/goods-service/api/spuController.service';
import { GoodsPageReqDTO } from '@app/api/goods-service/model/goodsPageReqDTO';
import { BlobConverterUtil } from '@app/shared/utils/blob-converter.util';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';

export interface ProductInfo {
  id: number;
  spuCode: string;
  spuName: string;
  categoryName: string;
  brandName: string;
  materialId: number;
  materialName: string;
  mainImage: string[];
  imageList: string[];
  // Additional fields from API response
  name?: string;
  type?: number;
  status?: number;
  setFlag?: number;
}

@Component({
  selector: 'app-goods-shop',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ToastModule,
    ButtonModule,
    DialogModule,
    ConfirmDialogModule,
    InputTextModule,
    DataTableComponent,
    AppButtonComponent
  ],

  templateUrl: './goods-shop.component.html',
  styleUrls: ['./goods-shop.component.css'],
})
export class GoodsShopComponent implements OnInit {

  
  // Form data for add/edit
  formData = signal<Partial<GoodsShop>>({
    spuId: undefined,
    spuCode: ''
  });
  
  // Table data
  tableData = signal<any[]>([]);
  
  // Loading state
  loading = signal(false);
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(10);

  // Table configuration
  tableColumns: TableColumn[] = [
    { prop: 'spuId', label: 'SPU ID', width: 100, type: 'text' },
    { prop: 'spuCode', label: 'SPU Code', width: 150, type: 'text' },
    { prop: 'shopId', label: 'Shop ID', width: 100, type: 'text' },
    { prop: 'status', label: 'Status', width: 100, type: 'custom', template: 'statusTemplate' },
    { prop: 'creationDate', label: 'Created Date', width: 180, type: 'custom', template: 'customTemplate' },
    { prop: 'actions', label: 'Actions', width: 150, type: 'actions', template: 'actionsTemplate' }
  ];

  // Shop ID from route
  shopId = signal<string | null>(null);
  shopName = signal<string>('');
  
  // SPU list for selection
  spuList = signal<any[]>([]);
  spuLoading = signal(false);

  // Dialog state
  showAddDialog = signal(false);

  // Search and filter terms
  searchTerm = signal('');
  categoryFilter = signal('');
  selectedProduct = signal<number | null>(null);

  // Computed properties
  filteredSpuList = computed(() => {
    return this.spuList().filter(spu => {
      const matchesSearchTerm = spu.code.toLowerCase().includes(this.searchTerm().toLowerCase());
      const matchesCategory = this.categoryFilter() === '' || spu.category.toLowerCase().includes(this.categoryFilter().toLowerCase());
      return matchesSearchTerm && matchesCategory;
    });
  });

  // Initial data for form
  initialData = computed(() => {
    return {
      spuId: undefined,
      spuCode: ''
    };
  });

  // Dialog loading state
  dialogLoading = signal(false);

  // Product selection modal
  productTableLoading = signal(false);
  selectedProductForTable = signal<ProductInfo | null>(null);
  productSearchForm = {
    productCode: '',
    productName: '',
  };

  // Pagination state for product selection
  productCurrentPage = signal(1);
  productPageSize = signal(20);
  productTotalRecords = signal(0);

  // Product options
  productOptions = signal<ProductInfo[]>([]);

  // Form configuration
  productTableColumns = computed(() => this.getProductTableColumns());

  // Since filtering is now handled by the API, we just return the products directly
  filteredProductOptions = computed(() => {
    return this.productOptions();
  });

  // Services
  private readonly goodsShopControllerService = inject(GoodsShopControllerService);
  private readonly spuControllerService = inject(SpuControllerService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  ngOnInit(): void {
    // Get shopId and shopName from route params
    this.route.params.subscribe(params => {
      const shopId = params['shopId'];
      const shopName = params['shopName'];
      
      if (shopId) {
        this.shopId.set(shopId);
        // Decode shop name from URL
        const decodedShopName = shopName ? decodeURIComponent(shopName) : 'Unknown Shop';
        this.shopName.set(decodedShopName);
        this.loadSpuList();
    this.loadInitialData();

      }
    });
    
    // Test API call
    this.testApiCall();
  }

  /**
   * Test API call to debug response format
   */
  private testApiCall(): void {
    const testRequest: GoodsPageReqDTO = {
      page: 0,
      size: 5,
      status: 1,
      setFlag: 0,
    };
    
    this.spuControllerService.pageGoods(testRequest).subscribe({
      next: async (response: any) => {
        try {
        
          // Use utility to convert response
          const jsonData = await BlobConverterUtil.convertToJson(response);
    
          // Extract and log page content
          const content = BlobConverterUtil.extractPageContent(jsonData);
          
        } catch (error) {
          console.error('Test API conversion error:', error);
        }
      },
      error: (error) => {
        console.error('Test API Error:', error);
      }
    });
  }

  /**
   * Load SPU list for selection
   */
  private loadSpuList(): void {
    this.spuLoading.set(true);
    
    // Load products from API
    const goodsPageReq: GoodsPageReqDTO = {
      page: 0,
      size: 100, // Load more products for the main list
      status: 1, // Only active products
      setFlag: 0, // Only ordinary products (not packages)
    };

    this.spuControllerService.pageGoods(goodsPageReq).subscribe({
      next: async (response: any) => {
        try {
          // Convert blob to JSON if needed
          const jsonData = await BlobConverterUtil.convertToJson(response);
          
          // Extract page content
          const content = BlobConverterUtil.extractPageContent(jsonData);
          
          // Map to the format expected by spuList
          const mappedSpuList = content.map((item: any) => ({
            id: item.id || 0,
            code: item.spuCode || '',
            name: item.name || '',
            category: item.categoryName || ''
          }));
          
          this.spuList.set(mappedSpuList);
          this.spuLoading.set(false);
  
        } catch (error) {
          console.error('Error processing SPU list response:', error);
          this.spuLoading.set(false);
          
          // Fallback to mock data if API fails
          const mockSpuList = [
            { id: 1, code: 'SPU001', name: 'Product 1', category: 'Electronics' },
            { id: 2, code: 'SPU002', name: 'Product 2', category: 'Clothing' },
            { id: 3, code: 'SPU003', name: 'Product 3', category: 'Home' }
          ];
          this.spuList.set(mockSpuList);
  
        }
      },
      error: (error) => {
        console.error('Error loading SPU list:', error);
        this.spuLoading.set(false);
        
        // Fallback to mock data if API fails
        const mockSpuList = [
          { id: 1, code: 'SPU001', name: 'Product 1', category: 'Electronics' },
          { id: 2, code: 'SPU002', name: 'Product 2', category: 'Clothing' },
          { id: 3, code: 'SPU003', name: 'Product 3', category: 'Home' }
        ];
        this.spuList.set(mockSpuList);

      }
    });
  }

  /**
   * Load initial data for the component
   */
  private loadInitialData(): void {
    const pageRequest: PageRequestDTO = {
      page: this.currentPage() - 1, // API uses 0-based indexing
      size: this.pageSize(),
      sortBy: 'id',
      ascending: false
    };
    
    const goodsShop: GoodsShop = {
      shopId: this.shopId() ? Number(this.shopId()) : undefined
    };
    
   
    this.loading.set(true);
    this.goodsShopControllerService.list7(goodsShop, pageRequest).subscribe({
      next: async (response) => {
        try {
         
          // Convert blob to JSON if needed
          const jsonData = await BlobConverterUtil.convertToJson(response);
         
          
          // Extract page content
          const content = BlobConverterUtil.extractPageContent(jsonData);
         
          
          // Map to GoodsShop format
          const items: GoodsShop[] = content.map((item: any) => ({
            id: item.id,
            spuId: item.spuId,
            spuCode: item.spuCode,
            shopId: item.shopId,
            status: item.status,
            deleteFlag: item.deleteFlag || 0,
            tenantId: item.tenantId || 0,
            objectVersionNumber: item.objectVersionNumber || 1,
            creationDate: item.creationDate,
            lastUpdateDate: item.lastUpdateDate,
            createdBy: item.createdBy,
            lastUpdatedBy: item.lastUpdatedBy
          }));
          
         
          this.tableData.set(items);
          this.totalRecords.set(BlobConverterUtil.extractTotalElements(jsonData));
          this.loading.set(false);
        } catch (error) {
          console.error('Error processing goods shop list response:', error);
        this.loading.set(false);
        }
      },
      error: (error) => {
        console.error('Error loading goods shop list:', error);
        this.loading.set(false);
      }
    });
  }



  /**
   * Open Add Product dialog
   */
  openAddDialog(): void {
    this.showAddDialog.set(true);
    this.selectedProductForTable.set(null);
    // Reset search form and load fresh data
    this.productSearchForm.productCode = '';
    this.productSearchForm.productName = '';
    this.productCurrentPage.set(1);
    this.loadProductsForModal();
  }

  /**
   * Close Add Product dialog
   */
  closeAddDialog(): void {
    this.showAddDialog.set(false);
    this.selectedProductForTable.set(null);
    // Clear the product options when modal is closed
    this.productOptions.set([]);
    this.resetForm();
  }

  /**
   * Handle form actions (save, cancel, etc.)
   */
  onFormAction(action: any): void {
    if (action.action === 'save') {
        this.handleSave(action.data);
    }
  }

  /**
   * Handle form value changes
   */
  onFormValueChanges(data: any): void {
    this.formData.set(data);
    // No need for auto-fill logic since all fields are manual input
  }



  /**
   * Handle table actions (edit, delete, etc.)
   */
  onTableAction(action: any): void {
    switch (action.action) {
      case 'edit':
        this.handleEdit(action.data);
        break;
      case 'delete':
        this.handleDelete(action.data);
        break;
      default:
    }
  }

  /**
   * Handle save action
   */
  private handleSave(data: any): void {
    if (!data) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No data to save'
      });
      return;
    }

    const goodsShop: GoodsShop = {
      spuId: data.spuId,
      spuCode: data.spuCode,
      shopId: data.shopId || (this.shopId() ? Number(this.shopId()) : undefined),
      status: data.status,
      deleteFlag: data.deleteFlag || 0,
      tenantId: data.tenantId || 0,
      objectVersionNumber: data.objectVersionNumber || 1
    };

    this.dialogLoading.set(true);
    
    this.goodsShopControllerService.create7(goodsShop).subscribe({
      next: () => {
        this.dialogLoading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Product store relationship created successfully'
        });
        this.closeAddDialog();
        this.loadInitialData();
      },
      error: () => {
        this.dialogLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to create product store relationship'
        });
      }
    });
  }

  /**
   * Handle edit action
   */
  handleEdit(data: any): void {
    // For now, just show the add dialog - edit functionality can be added later
    this.showAddDialog.set(true);
  }

  /**
   * Handle delete action
   */
  handleDelete(data: any): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this product store relationship?',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
      this.loading.set(true);
      this.goodsShopControllerService.remove6(data.id).subscribe({
        next: () => {
          this.loading.set(false);
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Product store relationship deleted successfully'
            });
          this.loadInitialData();
        },
        error: () => {
          this.loading.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete product store relationship'
            });
        }
      });
    }
    });
  }

  /**
   * Reset form to initial state
   */
  private resetForm(): void {
    this.formData.set({
      spuId: undefined,
      spuCode: '',
      status: undefined
    });
  }

  // Product selection modal methods
  private getProductTableColumns(): TableColumn[] {
    return [
      { prop: 'spuCode', label: 'Product Code', width: 150, type: 'text' },
      { prop: 'spuName', label: 'Product Name', width: 250, type: 'text' },
      {
        prop: 'categoryName',
        label: 'Product Category',
        width: 200,
        type: 'text',
      },
    ];
  }

  // Load products specifically for the modal - same as sku-create
  private loadProductsForModal(): void {
    this.productTableLoading.set(true);
    this.loadProductsFromAPI().subscribe({
      next: (products) => {
        this.productOptions.set(products);
        this.productTableLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading products for modal:', error);
        this.productOptions.set([]);
        this.productTableLoading.set(false);
      },
    });
  }

  // API method to fetch products - using blob converter utility
  private loadProductsFromAPI(): Observable<ProductInfo[]> {
    const goodsPageReq: GoodsPageReqDTO = {
      page: this.productCurrentPage() - 1, // API uses 0-based pagination
      size: this.productPageSize(),
      spuCode: this.productSearchForm.productCode || undefined,
      name: this.productSearchForm.productName || undefined,
      status: 1, // Only active products
      setFlag: 0, // Only ordinary products (not packages)
    };

    return this.spuControllerService.pageGoods(goodsPageReq).pipe(
      switchMap(async (response: any) => {
        try {
          // Convert blob to JSON if needed
          const jsonData = await BlobConverterUtil.convertToJson(response);
          
          // Set total records
          this.productTotalRecords.set(BlobConverterUtil.extractTotalElements(jsonData));
          
          // Extract page content
          const content = BlobConverterUtil.extractPageContent(jsonData);
          
          // Map products
          const mappedProducts = content.map((item: any) =>
            this.mapApiResponseToProductInfo(item)
          );
          
          return mappedProducts;
        } catch (error) {
          console.error('Error processing response:', error);
          return [];
        }
      }),
      catchError((error) => {
        console.error('Error fetching products:', error);
        return of([]);
      })
    );
  }

  // Map API response to ProductInfo interface - same as sku-create
  private mapApiResponseToProductInfo(apiItem: any): ProductInfo {
    const mapped = {
      id: apiItem.id || 0,
      spuCode: apiItem.spuCode || '',
      spuName: apiItem.name || '',
      categoryName: apiItem.categoryName || '',
      brandName: apiItem.brandName || '',
      materialId: apiItem.materialId || 0,
      materialName: apiItem.materialName || '',
      mainImage: apiItem.mainImage || [],
      imageList: [], // API doesn't provide imageList, using empty array
      attributeList: [], // API doesn't provide attributeList, using empty array
      attributeGroups: [], // API doesn't provide attributeList, using empty array
      // Additional fields
      name: apiItem.name,
      type: apiItem.type,
      setFlag: apiItem.setFlag,
      categoryId: apiItem.categoryId,
      brandId: apiItem.brandId,
      lastUpdatedBy: apiItem.lastUpdatedBy,
      lastUpdateDate: apiItem.lastUpdateDate,
      createdBy: apiItem.createdBy,
      createDate: apiItem.createDate,
      objectVersionNumber: apiItem.objectVersionNumber,
      status: apiItem.status,
      remark: apiItem.remark,
    };
    return mapped;
  }

  onProductSearch(): void {
    this.productCurrentPage.set(1); // Reset to first page
    this.loadProductsForModal();
  }

  onProductSearchReset(): void {
    this.productSearchForm.productCode = '';
    this.productSearchForm.productName = '';
    this.productCurrentPage.set(1); // Reset to first page
    this.loadProductsForModal(); // Reload products with reset filters
  }

  onProductPageChange(event: any): void {
    const newPage = Math.floor(event.first / this.productPageSize()) + 1;
    this.productCurrentPage.set(newPage);
    this.loadProductsForModal();
  }

  onProductTableSelectionChange(selection: ProductInfo[]): void {
    // Enforce single selection - only keep the last selected item - same as sku-create
    if (selection.length > 1) {
      // If multiple items are selected, only keep the last one
      const lastSelected = selection[selection.length - 1];
      this.selectedProductForTable.set(lastSelected);
    } else {
      // For single selection, take the first (and only) selected item
      this.selectedProductForTable.set(
        selection.length > 0 ? selection[0] : null
      );
    }
  }

  onProductSelectionConfirm(): void {
    const selected = this.selectedProductForTable();
    if (selected) {
      this.dialogLoading.set(true);
      
      // Create the goods shop relationship
      const goodsShopData: GoodsShop = {
        id: undefined,
        spuId: selected.id,
        spuCode: selected.spuCode,
        shopId: this.shopId() ? Number(this.shopId()) : undefined,
        status: 1, // Active
        deleteFlag: 0,
        tenantId: 0,
        objectVersionNumber: 1
      };

      this.goodsShopControllerService.create7(goodsShopData).subscribe({
        next: async (response) => {
          try {
            // Convert blob to JSON if needed
            const jsonData = await BlobConverterUtil.convertToJson(response);
            
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Product added to store successfully'
            });
            
            this.dialogLoading.set(false);
            this.closeAddDialog();
            this.loadInitialData(); // Reload the table
          } catch (error) {
            console.error('Error processing create response:', error);
            this.dialogLoading.set(false);
          }
        },
        error: (error) => {
          this.dialogLoading.set(false);
          console.error('Error adding product to store:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add product to store'
          });
        }
      });
    }
  }

  /**
   * Handle page change
   */
  onPageChange(event: any): void {
    const newPage = Math.floor(event.first / this.pageSize()) + 1;
    this.currentPage.set(newPage);
    this.loadInitialData();
  }

  /**
   * Handle cancel - navigate back to retail store list
   */
  onCancel(): void {
    this.router.navigate(['/channel/retail-store']);
  }

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;
}
